import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSticker } from '../context/StickerContext';
import { Header } from '../components/layout/Header';
import { Card } from '../components/ui/Card';
import { RadioListTwoColumn } from '../components/ui/RadioListTwoColumn';
import { RadioListOneColumn } from '../components/ui/RadioListOneColumn';
import { TileOptionGrid } from '../components/ui/TileOptionGrid';
import { UploadDropzone } from '../components/ui/UploadDropzone';
import { PrimaryButtonPurple } from '../components/ui/Buttons';
import { PriceSummaryRow } from '../components/ui/PriceSummaryRow';
import { Minus, Plus } from 'lucide-react';
export function StickersPage() {
  const navigate = useNavigate();
  const {
    quantity,
    setQuantity,
    size,
    setSize,
    material,
    setMaterial,
    lamination,
    setLamination,
    cutline,
    setCutline,
    uploadState,
    totalPrice,
    pricePerSticker
  } = useSticker();
  const quantityOptions = [50, 100, 250, 500, 1000, 2000, 'Custom quantity'];
  const sizeOptions = ['1 x 1 inch', '2 x 2 inch', '3 x 3 inch', '4 x 4 inch', '5 x 5 inch', '6 x 6 inch', '7 x 7 inch', '8 x 8 inch', '9 x 9 inch', '10 x 10 inch', 'Custom size'];
  const handleQuantityChange = (val: any) => {
    if (val === 'Custom quantity') {
      setQuantity(1000); // Default custom
    } else {
      setQuantity(Number(val));
    }
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-361">
      <Header data-id="element-362" />

      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-363">
        <h1 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-364">
          Custom Sticker - Materials and Lamination
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8" data-id="element-365">
          {/* LEFT COLUMN - CONFIGURATION */}
          <div className="lg:col-span-3 space-y-6" data-id="element-366">
            <Card title="Configuration" data-id="element-367">
              {/* Section 1: Quantity */}
              <div className="space-y-3" data-id="element-368">
                <label className="text-sm font-semibold text-gray-900" data-id="element-369">
                  Quantity of prints:
                </label>
                <RadioListTwoColumn name="quantity" options={quantityOptions} value={quantityOptions.includes(quantity) ? quantity : 'Custom quantity'} onChange={handleQuantityChange} data-id="element-370" />

                {/* Custom Quantity Input */}
                {!quantityOptions.includes(quantity) && <div className="mt-3" data-id="element-371">
                    <div className="relative" data-id="element-372">
                      <input type="number" value={quantity} onChange={e => setQuantity(Number(e.target.value))} className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-violet-500 outline-none" data-id="element-373" />
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col" data-id="element-374">
                        <button onClick={() => setQuantity(quantity + 1)} className="text-gray-400 hover:text-violet-600" data-id="element-375">
                          <Plus size={12} data-id="element-376" />
                        </button>
                        <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="text-gray-400 hover:text-violet-600" data-id="element-377">
                          <Minus size={12} data-id="element-378" />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-1" data-id="element-379">
                      The range of values is 1 to 2000
                    </p>
                  </div>}
              </div>

              <div className="border-t border-gray-100 my-6" data-id="element-380" />

              {/* Section 2: Size */}
              <div className="space-y-3" data-id="element-381">
                <label className="text-sm font-semibold text-gray-900" data-id="element-382">
                  Size of sticker:
                </label>
                <RadioListTwoColumn name="size" options={sizeOptions} value={size} onChange={setSize} data-id="element-383" />
              </div>

              <div className="border-t border-gray-100 my-6" data-id="element-384" />

              {/* Section 3: Material */}
              <div className="space-y-3" data-id="element-385">
                <label className="text-sm font-semibold text-gray-900" data-id="element-386">
                  Material:
                </label>
                <TileOptionGrid options={[{
                label: 'Permanent'
              }, {
                label: 'Removable'
              }]} value={material} onChange={setMaterial} data-id="element-387" />
                <p className="text-xs text-gray-400" data-id="element-388">
                  Scroll down for more options
                </p>
              </div>

              <div className="border-t border-gray-100 my-6" data-id="element-389" />

              {/* Section 4: Lamination */}
              <div className="space-y-3" data-id="element-390">
                <label className="text-sm font-semibold text-gray-900" data-id="element-391">
                  Lamination:
                </label>
                <TileOptionGrid options={[{
                label: 'Matte'
              }, {
                label: 'Gloss'
              }]} value={lamination} onChange={setLamination} data-id="element-392" />
              </div>

              <div className="border-t border-gray-100 my-6" data-id="element-393" />

              {/* Section 5: Cutline */}
              <div className="space-y-3" data-id="element-394">
                <label className="text-sm font-semibold text-gray-900" data-id="element-395">
                  Cutline:
                </label>
                <RadioListOneColumn name="cutline" options={['Cut to Outline (recommended)', 'Basic Shape', 'No Cutline']} value={cutline} onChange={val => setCutline(val as any)} data-id="element-396" />
                <p className="text-xs text-gray-400" data-id="element-397">
                  You can adjust cutline in the editor.
                </p>
              </div>

              <div className="border-t border-gray-100 mt-8 mb-4" data-id="element-398" />

              {/* Price Summary */}
              <PriceSummaryRow total={`$${totalPrice.toLocaleString('en-US', {
              minimumFractionDigits: 2
            })}`} perUnit={`$${pricePerSticker} per sticker`} data-id="element-399" />
            </Card>
          </div>

          {/* RIGHT COLUMN - UPLOAD */}
          <div className="lg:col-span-2 space-y-6" data-id="element-400">
            <Card title="Upload Your Design" className="sticky top-24" data-id="element-401">
              <UploadDropzone data-id="element-402" />

              <div className="mt-6" data-id="element-403">
                <PrimaryButtonPurple className="w-full" disabled={uploadState !== 'loaded'} onClick={() => navigate('/stickers/order-summary')} data-id="element-404">
                  Preview & Order
                </PrimaryButtonPurple>
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>;
}